/**
 * Cross-cutting helpers for turning an unknown thrown value into something
 * useful to humans and to the auto-bug-report pipeline.
 *
 * The motivating case (bug 76aa11fa, water-meter save): a non-Error object
 * reached a catch block whose default was `String(err)`, producing the famous
 * "[object Object]". We lost every shred of diagnostic information at the
 * catch site. `describeError` is the replacement: it always returns a real
 * message string, and when the thrown value is a structured object (Error,
 * ApiError, plain object) it surfaces the surrounding fields so the auto-bug
 * report carries enough context to actually debug.
 */

export interface ErrorDescription {
  message: string;
  name?: string;
  stack?: string;
  /** Present on ApiError (and any thrown Error that happens to carry a status). */
  status?: number;
  /** Present on ApiError (machine-readable error code from the server). */
  code?: string;
  /** JSON of the thrown value when it is a non-Error object — last-ditch context. */
  raw?: string;
}

function safeStringify(value: unknown): string | undefined {
  try {
    const seen = new WeakSet();
    const json = JSON.stringify(value, (_key, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v as object)) return '[Circular]';
        seen.add(v as object);
      }
      return v;
    });
    return json;
  } catch {
    return undefined;
  }
}

export function describeError(err: unknown): ErrorDescription {
  if (err instanceof Error) {
    const e = err as Error & { status?: unknown; code?: unknown };
    const status = typeof e.status === 'number' ? e.status : undefined;
    const code = typeof e.code === 'string' ? e.code : undefined;
    return {
      message: e.message || e.name || 'Error',
      name: e.name,
      stack: e.stack,
      ...(status !== undefined ? { status } : {}),
      ...(code !== undefined ? { code } : {}),
    };
  }
  if (err && typeof err === 'object') {
    const raw = safeStringify(err);
    return {
      message: raw && raw !== '{}' ? `Non-Error object thrown: ${raw}` : 'Non-Error object thrown',
      ...(raw ? { raw } : {}),
    };
  }
  if (err === undefined) return { message: 'undefined thrown' };
  if (err === null) return { message: 'null thrown' };
  return { message: String(err) };
}

/** Convenience for the common case: just give me the message string. */
export function errorMessage(err: unknown): string {
  return describeError(err).message;
}
